/**
 * Hooks de dados — TanStack Query.
 *
 * O que isto resolve em relação ao sistema atual:
 *  • cache e deduplicação: abrir a mesma OS duas vezes não refaz a requisição;
 *  • invalidação explícita depois de escrever, em vez de `searchOS()` de novo;
 *  • `refetchInterval` no monitor, que hoje exige F5 manual na TV da oficina;
 *  • estados de carregamento e erro tipados, sem `let carregando = true` solto.
 */
import { useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { acharStatus } from '@tcm/shared';
import type {
  Cliente,
  ConfigTermos,
  DefinicaoFluxo,
  ExecucaoAutomacao,
  ResumoExecucao,
  Conciliacao,
  Dashboard,
  ItemCatalogo,
  LancamentoCaixa,
  MonitorResposta,
  OsDetalhe,
  OsResumo,
  OsZerada,
  Paginado,
  PreviaMensagem,
  ResultadoEnvio,
  SaldoCaixa,
  StatusCatalogo,
  Tecnico,
  TemplateMensagem,
  UsuarioConta,
  UsuarioSessao,
  ConsultaPublica,
  LinkPublico,
  CriarUsuarioPayload,
  CodigoSecao,
  SalvarTermosPayload,
} from '@tcm/shared';
import { api, requisitar } from '../lib/api';

/** Chaves de cache num lugar só, para invalidar sem adivinhação. */
export const chaves = {
  os: ['os'] as const,
  osLista: (filtros: unknown) => ['os', 'lista', filtros] as const,
  osDetalhe: (osNumber: number) => ['os', 'detalhe', osNumber] as const,
  monitor: ['monitor'] as const,
  clientes: (filtros: unknown) => ['clientes', filtros] as const,
  historico: (telefone: string) => ['clientes', telefone, 'os'] as const,
  caixa: (filtros: unknown) => ['caixa', filtros] as const,
  conciliacao: ['caixa', 'conciliacao'] as const,
  dashboard: (periodo: unknown) => ['relatorios', 'dashboard', periodo] as const,
  osZeradas: ['relatorios', 'os-zeradas'] as const,
  catalogo: (nome: string) => ['catalogos', nome] as const,
  fluxos: ['automacoes', 'fluxos'] as const,
  execucoes: (limite: number, fluxo?: string) =>
    ['automacoes', 'execucoes', limite, fluxo ?? 'todos'] as const,
  execucao: (id: string) => ['automacoes', 'execucoes', id] as const,
  sessao: ['auth', 'me'] as const,
  contas: ['usuarios'] as const,
  termos: ['configuracoes', 'termos'] as const,
  linkPublico: (osNumber: number) => ['os', osNumber, 'link-publico'] as const,
  consultaPublica: (token: string) => ['publico', token] as const,
};

/* ------------------------------------------------------------------ */
/* Ordens de serviço                                                   */
/* ------------------------------------------------------------------ */

export interface FiltrosOs {
  page: number;
  limit: number;
  q?: string;
  osNumber?: number;
  status?: string;
  tecnicoId?: number;
  dataInicio?: string;
  dataFim?: string;
}

export function useOrdens(filtros: FiltrosOs) {
  return useQuery({
    queryKey: chaves.osLista(filtros),
    queryFn: ({ signal }) => api.get<Paginado<OsResumo>>('/ordens-servico', { ...filtros }, signal),
    // Mantém a página anterior visível durante a troca — evita o "pisca" que
    // a paginação atual provoca a cada clique.
    placeholderData: keepPreviousData,
  });
}

/**
 * Opções da consulta de detalhe, separadas do hook porque a geração do PDF
 * busca a mesma OS fora de um componente, com `fetchQuery`. Compartilhar a
 * chave e a função é o que faz o resultado cair no MESMO ponto do cache que a
 * tela lê — a ficha aberta atrás da aba do PDF não fica mostrando outra coisa.
 */
export function consultaOsDetalhe(osNumber: number) {
  return {
    queryKey: chaves.osDetalhe(osNumber),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.get<OsDetalhe>(`/ordens-servico/${osNumber}`, undefined, signal),
  };
}

export function useOs(osNumber: number | undefined): UseQueryResult<OsDetalhe> {
  return useQuery({
    ...consultaOsDetalhe(osNumber ?? 0),
    enabled: Number.isFinite(osNumber) && (osNumber ?? 0) > 0,
  });
}

/** Monitor de parede: atualiza sozinho a cada 30 s. */
export function useMonitor(intervaloMs = 30_000) {
  return useQuery({
    queryKey: chaves.monitor,
    queryFn: ({ signal }) => api.get<MonitorResposta>('/ordens-servico/monitor', undefined, signal),
    refetchInterval: intervaloMs,
    refetchIntervalInBackground: false,
  });
}

export function useCriarOs() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: unknown) => api.post<OsDetalhe>('/ordens-servico', dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.os });
      void cliente.invalidateQueries({ queryKey: chaves.monitor });
    },
  });
}

export function useAtualizarStatus(osNumber: number) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: unknown) => api.patch<OsDetalhe>(`/ordens-servico/${osNumber}/status`, dados),
    onSuccess: (detalhe) => {
      cliente.setQueryData(chaves.osDetalhe(osNumber), detalhe);
      void cliente.invalidateQueries({ queryKey: chaves.monitor });
      // O pagamento entrou no caixa na mesma transação: o extrato mudou também.
      void cliente.invalidateQueries({ queryKey: ['caixa'] });
    },
  });
}

export interface MoverStatusVars {
  osNumber: number;
  status: string;
  saida?: string | null;
  pagamento?: { valorCentavos: number; forma: string; parcelas?: number } | null;
}

/**
 * Troca o status de uma OS qualquer — é o que o arrastar-e-soltar do quadro usa.
 *
 * Difere de `useAtualizarStatus` em duas coisas: o número da OS vem na chamada
 * (o quadro move qualquer cartão) e a mudança é otimista. Sem isso o cartão
 * ficaria na coluna de origem até o refetch do monitor chegar, e o operador
 * arrastaria de novo achando que não pegou. Se o servidor recusar, a lista
 * anterior volta inteira.
 */
export function useMoverStatus() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ osNumber, ...corpo }: MoverStatusVars) =>
      api.patch<OsDetalhe>(`/ordens-servico/${osNumber}/status`, corpo),

    onMutate: async ({ osNumber, status }) => {
      // O monitor busca sozinho a cada 30 s: uma resposta em voo sobrescreveria
      // o movimento otimista logo depois de aplicá-lo.
      await cliente.cancelQueries({ queryKey: chaves.monitor });
      const anterior = cliente.getQueryData<MonitorResposta>(chaves.monitor);

      cliente.setQueryData<MonitorResposta>(chaves.monitor, (atual) =>
        atual ? moverNoMonitor(atual, osNumber, status) : atual,
      );

      return { anterior };
    },

    onError: (_erro, _vars, contexto) => {
      if (contexto?.anterior) cliente.setQueryData(chaves.monitor, contexto.anterior);
    },

    onSuccess: (detalhe, vars) => {
      cliente.setQueryData(chaves.osDetalhe(vars.osNumber), detalhe);
    },

    onSettled: () => {
      void cliente.invalidateQueries({ queryKey: chaves.monitor });
      void cliente.invalidateQueries({ queryKey: chaves.os });
      // O pagamento entrou no caixa na mesma transação: o extrato mudou também.
      void cliente.invalidateQueries({ queryKey: ['caixa'] });
    },
  });
}

/** Reescreve o cartão na resposta em cache, sem esperar o servidor. */
function moverNoMonitor(atual: MonitorResposta, osNumber: number, codigo: string): MonitorResposta {
  const novo = acharStatus(codigo);
  let removidas = 0;

  const colunas = atual.colunas.map((coluna) => ({
    ...coluna,
    ordens: coluna.ordens.flatMap((os) => {
      if (os.osNumber !== osNumber) return [os];
      // Status finalizador tira a OS do monitor — a regra é a mesma do servidor.
      if (novo.finalizaOs) {
        removidas += 1;
        return [];
      }
      return [
        {
          ...os,
          status: novo.codigo,
          statusRotulo: novo.rotulo,
          statusCategoria: novo.categoria,
          cor: novo.cor,
        },
      ];
    }),
  }));

  return { ...atual, colunas, total: atual.total - removidas };
}

export function useSalvarOrcamento(osNumber: number) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: unknown) => api.put<OsDetalhe>(`/ordens-servico/${osNumber}/orcamento`, dados),
    onSuccess: (detalhe) => {
      cliente.setQueryData(chaves.osDetalhe(osNumber), detalhe);
      void cliente.invalidateQueries({ queryKey: chaves.osZeradas });
    },
  });
}

export function useImprimirOs(osNumber: number) {
  return useMutation({
    mutationFn: () =>
      api.post<{ enviado: boolean; simulado: boolean }>(`/ordens-servico/${osNumber}/imprimir`),
  });
}

export function useContatoPorTelefone() {
  return useMutation({
    mutationFn: (telefone: string) =>
      api.get<{ nome: string; sobrenome: string; telefone: string }>(
        `/ordens-servico/contatos/${encodeURIComponent(telefone)}`,
      ),
  });
}

/* ------------------------------------------------------------------ */
/* Clientes                                                            */
/* ------------------------------------------------------------------ */

export function useClientes(filtros: { page: number; limit: number; q?: string }) {
  return useQuery({
    queryKey: chaves.clientes(filtros),
    queryFn: ({ signal }) => api.get<Paginado<Cliente>>('/clientes', { ...filtros }, signal),
    placeholderData: keepPreviousData,
  });
}

export function useHistoricoCliente(telefone: string | undefined) {
  return useQuery({
    queryKey: chaves.historico(telefone ?? ''),
    queryFn: ({ signal }) =>
      api.get<{ data: OsResumo[] }>(
        `/clientes/${encodeURIComponent(telefone ?? '')}/ordens-servico`,
        undefined,
        signal,
      ),
    enabled: Boolean(telefone),
  });
}

/* ------------------------------------------------------------------ */
/* Caixa                                                               */
/* ------------------------------------------------------------------ */

export interface FiltrosCaixa {
  page: number;
  limit: number;
  dataInicio?: string;
  dataFim?: string;
  tipo?: 'entrada' | 'saida';
  origem?: 'os' | 'venda';
  osNumber?: number;
}

export function useCaixa(filtros: FiltrosCaixa) {
  return useQuery({
    queryKey: chaves.caixa(filtros),
    queryFn: ({ signal }) =>
      api.get<Paginado<LancamentoCaixa> & { saldo: SaldoCaixa }>('/caixa', { ...filtros }, signal),
    placeholderData: keepPreviousData,
  });
}

export function useLancarCaixa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: unknown) => api.post<LancamentoCaixa>('/caixa', dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['caixa'] });
      void cliente.invalidateQueries({ queryKey: chaves.os });
    },
  });
}

export function useConciliacao() {
  return useQuery({
    queryKey: chaves.conciliacao,
    queryFn: ({ signal }) => api.get<Conciliacao>('/caixa/conciliacao', undefined, signal),
  });
}

/* ------------------------------------------------------------------ */
/* Relatórios                                                          */
/* ------------------------------------------------------------------ */

export function useDashboard(periodo: { dataInicio?: string; dataFim?: string }) {
  return useQuery({
    queryKey: chaves.dashboard(periodo),
    queryFn: ({ signal }) => api.get<Dashboard>('/relatorios/dashboard', { ...periodo }, signal),
  });
}

export function useOsZeradas() {
  return useQuery({
    queryKey: chaves.osZeradas,
    queryFn: ({ signal }) =>
      api.get<{ data: OsZerada[] }>('/relatorios/os-zeradas', undefined, signal),
  });
}

/* ------------------------------------------------------------------ */
/* Catálogos                                                           */
/* ------------------------------------------------------------------ */

function useCatalogo<T>(nome: string, caminho: string) {
  return useQuery({
    queryKey: chaves.catalogo(nome),
    queryFn: ({ signal }) => api.get<{ data: T[] }>(caminho, undefined, signal),
    // Catálogos quase não mudam: 30 min sem revalidar.
    staleTime: 30 * 60 * 1000,
  });
}

export const useAparelhos = () => useCatalogo<ItemCatalogo>('aparelhos', '/catalogos/aparelhos');
export const useMarcas = () => useCatalogo<ItemCatalogo>('marcas', '/catalogos/marcas');
export const useStatusCatalogo = () => useCatalogo<StatusCatalogo>('status', '/catalogos/status');
export const useTecnicos = () => useCatalogo<Tecnico>('tecnicos', '/catalogos/tecnicos');

/**
 * Aquece os catálogos de aparelho e marca assim que a casca do sistema monta.
 *
 * Eles não são de nenhuma tela em particular: são as duas listas que "Nova
 * OS", "Orçamento" e o modal de edição abrem preenchidas. Buscados só quando
 * a tela monta, viram ~300 ms de campo vazio em cima do túnel que serve a
 * loja — toda vez que alguém vai cadastrar um aparelho.
 *
 * Roda em tempo OCIOSO, e essa é a parte que importa: a tela que o operador
 * está olhando agora tem prioridade sobre a que ele talvez abra em seguida.
 * Com `staleTime` de 30 min, é uma vez por sessão, não por navegação.
 *
 * Só aparelhos e marcas: `status`, `tecnicos` e `formas-pagamento` são
 * constantes e a API já manda o navegador guardá-las por dez minutos.
 */
export function useAquecerCatalogos(): void {
  const cliente = useQueryClient();

  useEffect(() => {
    const aquecer = (): void => {
      for (const [nome, caminho] of [
        ['aparelhos', '/catalogos/aparelhos'],
        ['marcas', '/catalogos/marcas'],
      ] as const) {
        void cliente
          .prefetchQuery({
            queryKey: chaves.catalogo(nome),
            queryFn: ({ signal }) => api.get<{ data: ItemCatalogo[] }>(caminho, undefined, signal),
            staleTime: 30 * 60 * 1000,
          })
          // Falhar aqui não é problema de ninguém: a tela que precisar da
          // lista pede de novo e mostra o próprio erro.
          .catch(() => undefined);
      }
    };

    // `requestIdleCallback` não existe no Safari antigo do balcão; o timeout
    // é o mesmo compromisso — depois da primeira tela, não durante.
    // `typeof` e não `'requestIdleCallback' in window`: para o TypeScript a
    // propriedade sempre existe, então o `in` deixaria o outro ramo como
    // código morto — e ele não é, é o Safari mais velho do balcão.
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(aquecer, { timeout: 3_000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(aquecer, 1_500);
    return () => window.clearTimeout(id);
  }, [cliente]);
}

export function useCriarItemCatalogo(tipo: 'aparelhos' | 'marcas') {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (nome: string) => api.post<ItemCatalogo>(`/catalogos/${tipo}`, { nome }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.catalogo(tipo) });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Mensagens                                                           */
/* ------------------------------------------------------------------ */

export function useTemplates() {
  return useQuery({
    queryKey: ['mensagens', 'templates'],
    queryFn: ({ signal }) =>
      api.get<{ data: TemplateMensagem[] }>('/mensagens/templates', undefined, signal),
    staleTime: 30 * 60 * 1000,
  });
}

export function usePreviaMensagem() {
  return useMutation({
    mutationFn: (dados: unknown) => api.post<PreviaMensagem>('/mensagens/previa', dados),
  });
}

/**
 * Prévia que acompanha a digitação, para a tela de orçamento.
 *
 * É `useQuery` e não `useMutation` por causa da ordem: com mutação, duas
 * teclas seguidas disparam duas requisições e a que voltar por último vence —
 * que pode ser a mais antiga. Aqui o corpo é a chave de cache, o Query cancela
 * a anterior pelo `signal` e o texto exibido é sempre o do corpo atual.
 *
 * `placeholderData` mantém o balão anterior visível durante a troca, em vez de
 * piscar em branco a cada letra digitada.
 */
export function usePreviaAoVivo(corpo: unknown | null) {
  return useQuery({
    queryKey: ['mensagens', 'previa', corpo],
    queryFn: ({ signal }) =>
      requisitar<PreviaMensagem>('/mensagens/previa', { metodo: 'POST', corpo, sinal: signal }),
    enabled: corpo != null,
    placeholderData: keepPreviousData,
    // Texto renderizado a partir de um corpo idêntico não muda: não há por que
    // refazer a requisição ao voltar para a aba.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useEnviarMensagem() {
  return useMutation({
    mutationFn: (dados: unknown) => api.post<ResultadoEnvio>('/mensagens/enviar', dados),
  });
}

/* ------------------------------------------------------------------ */
/* Termos de serviço — Admin                                           */
/* ------------------------------------------------------------------ */

/**
 * O texto que vai junto com a OS no WhatsApp, e de onde ele vem.
 *
 * `staleTime: 0` de propósito, ao contrário dos templates: esta é a tela em
 * que alguém acabou de salvar, e mostrar um cache de trinta minutos aqui
 * seria mostrar o texto anterior a quem quer conferir o que gravou.
 */
export function useTermos() {
  return useQuery({
    queryKey: chaves.termos,
    queryFn: ({ signal }) => api.get<ConfigTermos>('/configuracoes/termos', undefined, signal),
  });
}

/**
 * Salvar e restaurar respondem com a configuração JÁ RESOLVIDA pelo
 * servidor — a mesma coisa que o GET devolveria. Por isso o resultado é
 * escrito direto no cache em vez de disparar uma segunda ida à rede: o que
 * a tela mostra depois de salvar é o que o cliente vai receber, não o que o
 * campo tinha digitado.
 */
function useMutacaoDeTermos<T>(mutationFn: (dados: T) => Promise<ConfigTermos>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (config) => qc.setQueryData(chaves.termos, config),
  });
}

export function useSalvarTermos() {
  return useMutacaoDeTermos((dados: SalvarTermosPayload) =>
    api.put<ConfigTermos>('/configuracoes/termos', dados),
  );
}

export function useRestaurarTermos() {
  return useMutacaoDeTermos<void>(() => api.delete<ConfigTermos>('/configuracoes/termos'));
}

/* ------------------------------------------------------------------ */
/* Sessão                                                              */
/* ------------------------------------------------------------------ */

/**
 * `habilitado = false` na consulta pública do cliente: aquela tela não tem
 * dono de sessão, e perguntar `/auth/me` ali seria um 401 garantido no
 * console de quem só quer saber onde está o aparelho.
 */
export function useSessao(habilitado = true) {
  return useQuery({
    queryKey: chaves.sessao,
    queryFn: ({ signal }) => api.get<{ usuario: UsuarioSessao }>('/auth/me', undefined, signal),
    enabled: habilitado,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * O token vem em cookie httpOnly: o JavaScript não vê nem guarda nada. O que
 * a tela precisa fazer depois de entrar ou sair é limpar o cache — inclusive
 * o das outras telas, para que dados de uma sessão não sobrevivam à próxima.
 */
export function useEntrar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { usuario: string; senha: string }) =>
      api.post<{ autenticado: boolean; usuario: UsuarioSessao }>('/auth/login', dados),
    onSuccess: () => qc.clear(),
  });
}

export function useSair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ autenticado: false }>('/auth/logout'),
    onSuccess: () => qc.clear(),
  });
}

export function useTrocarSenha() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { senhaAtual: string; senhaNova: string }) =>
      api.post<{ alterada: boolean }>('/auth/senha', dados),
    onSuccess: () => qc.invalidateQueries({ queryKey: chaves.sessao }),
  });
}

/* ------------------------------------------------------------------ */
/* Contas de acesso — só respondem para papel `admin`                  */
/* ------------------------------------------------------------------ */

export function useContas(habilitado = true) {
  return useQuery({
    queryKey: chaves.contas,
    queryFn: ({ signal }) => api.get<{ data: UsuarioConta[] }>('/usuarios', undefined, signal),
    // Sem isto, um operador que abrisse a URL da tela dispararia um 403 a
    // cada montagem só para receber a mesma negativa.
    enabled: habilitado,
    staleTime: 60_000,
  });
}

/** Toda escrita de conta reflete na mesma lista — uma invalidação só. */
function useMutacaoDeConta<T>(mutationFn: (dados: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: chaves.contas }),
  });
}

export function useCriarConta() {
  return useMutacaoDeConta((dados: CriarUsuarioPayload) =>
    api.post<UsuarioConta>('/usuarios', dados),
  );
}

export function useRedefinirSenha() {
  return useMutacaoDeConta((dados: { login: string; senhaNova: string }) =>
    api.post<UsuarioConta>(`/usuarios/${dados.login}/senha`, { senhaNova: dados.senhaNova }),
  );
}

export function useDefinirAtivo() {
  return useMutacaoDeConta((dados: { login: string; ativo: boolean }) =>
    api.patch<UsuarioConta>(`/usuarios/${dados.login}/ativo`, { ativo: dados.ativo }),
  );
}

/**
 * Seções do menu daquela conta. `secoes: null` devolve ao padrão do perfil.
 *
 * Invalida a sessão junto com a lista: quando o administrador mexe na própria
 * conta pela API (o que a rota recusa hoje) ou quando a resposta muda o que
 * `/auth/me` diria, o menu não pode continuar mostrando o estado anterior.
 */
export function useDefinirSecoes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { login: string; secoes: CodigoSecao[] | null }) =>
      api.patch<UsuarioConta>(`/usuarios/${dados.login}/secoes`, { secoes: dados.secoes }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: chaves.contas });
      await qc.invalidateQueries({ queryKey: chaves.sessao });
    },
  });
}

export function useDesbloquearConta() {
  return useMutacaoDeConta((login: string) =>
    api.post<UsuarioConta>(`/usuarios/${login}/desbloquear`),
  );
}

/* ------------------------------------------------------------------ */
/* Automações                                                          */
/* ------------------------------------------------------------------ */

/** Desenho do fluxo + o que está ligado no ambiente. Praticamente estático. */
export function useFluxos() {
  return useQuery({
    queryKey: chaves.fluxos,
    queryFn: ({ signal }) =>
      api.get<{ data: DefinicaoFluxo[]; integracoes: { zapi: boolean; n8n: boolean } }>(
        '/automacoes/fluxos',
        undefined,
        signal,
      ),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Últimas execuções. Atualiza sozinha: a tela costuma ficar aberta enquanto
 * alguém gera uma OS no balcão para acompanhar o fluxo acontecendo.
 */
export function useExecucoes(limite = 20, fluxo?: string, intervaloMs = 10_000) {
  return useQuery({
    queryKey: chaves.execucoes(limite, fluxo),
    queryFn: ({ signal }) =>
      api.get<{ data: ResumoExecucao[] }>(
        '/automacoes/execucoes',
        fluxo ? { limit: limite, fluxo } : { limit: limite },
        signal,
      ),
    refetchInterval: intervaloMs,
    refetchIntervalInBackground: false,
  });
}

/** Uma execução com o payload de cada nó. */
export function useExecucao(id: string | undefined) {
  return useQuery({
    queryKey: chaves.execucao(id ?? ''),
    queryFn: ({ signal }) =>
      api.get<ExecucaoAutomacao>(`/automacoes/execucoes/${id}`, undefined, signal),
    enabled: Boolean(id),
    // O rastro de uma execução nunca muda depois de gravado.
    staleTime: Infinity,
  });
}

/* ------------------------------------------------------------------ */
/* Consulta pública                                                    */
/* ------------------------------------------------------------------ */

/**
 * A página que o CLIENTE abre. É a única chamada do frontend que não
 * depende de sessão — e a única que roda com o `QueryClient` fora do app
 * logado.
 *
 * `retry: false` porque o erro esperado aqui é 404 (link revogado, token
 * errado, endereço digitado à mão), e insistir três vezes num link que não
 * existe só faz o cliente esperar para receber a mesma resposta.
 */
export function useConsultaPublica(token: string | undefined) {
  return useQuery({
    queryKey: chaves.consultaPublica(token ?? ''),
    queryFn: ({ signal }) =>
      api.get<ConsultaPublica>(`/publico/os/${token}`, undefined, signal),
    enabled: Boolean(token),
    retry: false,
    // O status muda no balcão enquanto a aba do cliente fica aberta no
    // bolso dele. Voltar para a aba relê — sem timer batendo à toa.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/** O link atual da OS, na ficha. `link: null` = nunca foi emitido. */
export function useLinkPublico(osNumber: number | undefined) {
  return useQuery({
    queryKey: chaves.linkPublico(osNumber ?? 0),
    queryFn: ({ signal }) =>
      api.get<{ link: LinkPublico | null }>(
        `/ordens-servico/${osNumber}/link-publico`,
        undefined,
        signal,
      ),
    enabled: Number.isFinite(osNumber) && (osNumber ?? 0) > 0,
  });
}

export function useEmitirLinkPublico(osNumber: number) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (rotacionar: boolean) =>
      api.post<LinkPublico>(`/ordens-servico/${osNumber}/link-publico`, { rotacionar }),
    onSuccess: (link) => cliente.setQueryData(chaves.linkPublico(osNumber), { link }),
  });
}

export function useRevogarLinkPublico(osNumber: number) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ link: LinkPublico | null }>(`/ordens-servico/${osNumber}/link-publico`),
    onSuccess: (resposta) => cliente.setQueryData(chaves.linkPublico(osNumber), resposta),
  });
}
